import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  User,
  Flame,
  Target,
  Calendar,
  TrendingUp,
  Trophy,
  Clock,
  Activity,
  Heart,
  Brain,
  CheckCircle,
  Mail,
  Crown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

const MOOD_EMOJIS = {
  tired: "😴",
  stressed: "😰",
  unmotivated: "😔",
  neutral: "😐",
  motivated: "💪",
  energized: "⚡",
  unstoppable: "🔥"
};

export default function AdminUserProgress() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/progress`);
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/admin");
      } else if (error.response?.status === 404) {
        toast.error("User not found");
        navigate("/admin/users");
      }
      toast.error("Failed to load user progress");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  if (!data) return null;

  const { user, statistics, workout_history, workout_by_day, metrics_history, mood_history, challenges } = data;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/admin/users" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Back to Users
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">USER PROGRESS</span>
            </div>
            <div className="w-24" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* User Info Card */}
        <Card className="bg-card border-border mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-3xl font-black text-primary">
                  {user.name?.[0]?.toUpperCase() || '?'}
                </span>
              </div>
              
              <div className="flex-1">
                <h1 className="text-2xl font-black uppercase tracking-tighter">{user.name}</h1>
                <p className="text-muted-foreground flex items-center gap-2">
                  <Mail className="h-4 w-4" /> {user.email}
                </p>
                <div className="flex flex-wrap gap-3 mt-2">
                  {user.subscription_tier ? (
                    <span className="px-3 py-1 bg-accent/20 text-accent rounded-full text-xs font-bold uppercase flex items-center gap-1">
                      <Crown className="h-3 w-3" /> {user.subscription_tier}
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-xs font-bold uppercase">
                      Free Trial
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    Joined {new Date(user.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-black text-primary font-mono">{user.current_day}</div>
                  <div className="text-xs text-muted-foreground uppercase">Day</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-orange-500 font-mono">{user.streak}</div>
                  <div className="text-xs text-muted-foreground uppercase">Streak</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-accent font-mono">{user.longest_streak || 0}</div>
                  <div className="text-xs text-muted-foreground uppercase">Best</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30">
            <CardContent className="p-6 text-center">
              <Activity className="h-8 w-8 text-primary mx-auto mb-2" />
              <div className="text-4xl font-black text-primary">{statistics.total_workouts}</div>
              <div className="text-sm text-muted-foreground">Total Workouts</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-accent/20 to-accent/5 border-accent/30">
            <CardContent className="p-6 text-center">
              <TrendingUp className="h-8 w-8 text-accent mx-auto mb-2" />
              <div className="text-4xl font-black text-accent">{statistics.completion_rate}%</div>
              <div className="text-sm text-muted-foreground">Completion Rate</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500/20 to-orange-500/5 border-orange-500/30">
            <CardContent className="p-6 text-center">
              <Calendar className="h-8 w-8 text-orange-400 mx-auto mb-2" />
              <div className="text-4xl font-black text-orange-400">{statistics.average_workouts_per_week}</div>
              <div className="text-sm text-muted-foreground">Avg/Week</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Workout History */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-accent" />
                Recent Workouts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {workout_history?.map((workout, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
                        <span className="font-bold text-accent">D{workout.day}</span>
                      </div>
                      <div>
                        <p className="font-bold text-sm">{workout.workout_title}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(workout.completed_at).toLocaleDateString()} at {new Date(workout.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                    <CheckCircle className="h-5 w-5 text-accent" />
                  </motion.div>
                ))}
                
                {(!workout_history || workout_history.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">No workouts completed yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Workout by Day of Week */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Workout Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                  const count = workout_by_day?.[day] || 0;
                  const maxCount = Math.max(...Object.values(workout_by_day || { x: 1 }), 1);
                  return (
                    <div key={day} className="flex items-center gap-3">
                      <span className="w-24 text-sm font-medium">{day.slice(0, 3)}</span>
                      <div className="flex-1 bg-secondary rounded-full h-4 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(count / maxCount) * 100}%` }}
                          className="h-full bg-primary"
                        />
                      </div>
                      <span className="font-mono text-sm w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Mood History */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-500" />
                Mood Check-ins
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {mood_history?.map((mood, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{MOOD_EMOJIS[mood.mood] || '😐'}</span>
                      <div>
                        <p className="font-bold text-sm capitalize">{mood.mood}</p>
                        <p className="text-xs text-muted-foreground">{new Date(mood.date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold">Energy: {mood.energy}/10</div>
                    </div>
                  </div>
                ))}
                
                {(!mood_history || mood_history.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">No mood check-ins yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Challenges */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Weekly Challenges
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {challenges?.map((challenge, i) => (
                  <div key={i} className="p-3 bg-secondary/30 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-sm">Week {challenge.week_number}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${challenge.completed ? 'bg-accent/20 text-accent' : 'bg-orange-500/20 text-orange-500'}`}>
                        {challenge.completed ? 'Completed' : 'In Progress'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground capitalize">{challenge.challenge_type?.replace('_', ' ')}</p>
                    <div className="mt-2">
                      <Progress value={(challenge.progress / challenge.target) * 100} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1">{challenge.progress}/{challenge.target}</p>
                    </div>
                  </div>
                ))}
                
                {(!challenges || challenges.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">No challenges yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
